import * as Tables from 'state/tables';
import { Features, core } from '@tock/coretypes';
import { LocalDate } from 'js-joda';
import { Selector, createSelector } from 'reselect';
import { StoreType } from '@tock/shared/utils/connect';
import { TableCombinationFilled } from 'selectors/tableCombinationSelector';
import { activeTicketsByIdSelector } from 'selectors/experienceSelectors';
import { businessTime, fastParseTime } from 'utils/timeUtils';
import { dashboard } from 'types';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { getSectionLabelForTable } from 'utils/tableBasedTemplatesUtils';
import { toProtoWeekdayNumber, weekdayNumber } from '@tock/shared/utils/time';
import Long from 'long';
import _ from 'lodash';
import natsort from 'natsort';

export interface TableIdMap {
  [tableId: string]: dashboard.Table;
}

export interface TableIdToSection {
  [tableId: number]: string;
}

export interface RoomSectionToActiveCalendarFloorplanTableIds {
  [roomSection: string]: number[];
}

export interface CalendarFloorplan {
  activeTables: dashboard.Table[];
  allTableCombinations: dashboard.TableCombination[];
  archivedTables: dashboard.Table[];
}

export const calendarFloorplanStateSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan,
  (calendarFloorplanState): StoreType['calendarFloorplan'] => calendarFloorplanState
);

export const archivedCalendarFloorplanTablesSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.archivedTables,
  (archivedTables) => archivedTables
);

export const allCalendarFloorplanTableCombinationsSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.tableCombinations,
  (tableCombinations) => tableCombinations
);

export const activeCalendarFloorplanTablesSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.activeTables,
  (activeTables) => activeTables
);

export const activeAndArchivedCalendarFloorplanTablesSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.activeTables,
  (state: StoreType) => state.calendarFloorplan.archivedTables,
  (activeTables, archivedTables) => [...activeTables, ...archivedTables]
);

export const activeCalendarFloorplanTablesByIdSelector = createSelector(
  activeCalendarFloorplanTablesSelector,
  (tables): TableIdMap => {
    return _.reduce(
      tables,
      (tableIdToTableMap: TableIdMap, table) => {
        tableIdToTableMap[table.id] = table;
        return tableIdToTableMap;
      },
      {}
    );
  }
);

export const activeAndArchivedCalendarFloorplanTablesbyIdSelector = createSelector(
  activeCalendarFloorplanTablesSelector,
  archivedCalendarFloorplanTablesSelector,
  (activeTables: dashboard.Table[], archivedTables: dashboard.Table[]): TableIdMap => {
    return _.keyBy<dashboard.Table>(
      _.concat(activeTables, archivedTables),
      (table: dashboard.Table) => table.id
    );
  }
);

export const calendarFloorplanTablePrioritiesByTableIdSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.tablePrioritiesByTableId,
  (tablePrioritiesByTableId) => tablePrioritiesByTableId
);

function getTableIdToSectionMap(
  tables: Array<{ id: number; room: string; section: string }>
): TableIdToSection {
  const tableIdToSectionMap: TableIdToSection = {};

  for (const table of tables) {
    tableIdToSectionMap[table.id] = getSectionLabelForTable(table);
  }

  return tableIdToSectionMap;
}

export const activeCalendarFloorPlanTableIdToSectionSelector = createSelector(
  activeCalendarFloorplanTablesSelector,
  getTableIdToSectionMap
);

export const activeAndArchivedCalendarFloorPlanTableIdToSectionSelector = createSelector(
  activeAndArchivedCalendarFloorplanTablesSelector,
  getTableIdToSectionMap
);

export const roomSectionToActiveCalendarFloorplanTableIdSelector = createSelector(
  activeCalendarFloorplanTablesSelector,
  (tables): RoomSectionToActiveCalendarFloorplanTableIds => {
    const tablesByRoomSection = _.groupBy(tables, (table) => {
      return table.room + ', ' + table.section;
    });
    return _.mapValues(tablesByRoomSection, (tables) => {
      return _.map(tables, (table) => table.id);
    });
  }
);

/**
 * Merges table metadata with table combinations.
 * Returns active table combinations for the currently loaded calendar
 * floor plan.
 */
export const calendarFloorplanFilledTableCombinations = createSelector(
  activeAndArchivedCalendarFloorplanTablesbyIdSelector,
  allCalendarFloorplanTableCombinationsSelector,
  (tablesById, tableCombos): TableCombinationFilled[] => {
    const tableNameSort = natsort();
    return tableCombos
      .map((combo) => ({
        ...combo,
        tables: combo.tableId
          .map((id) => ({ ...tablesById[id], id: Long.fromNumber(id) }) as Tables.Table)
          .sort((a, b) => tableNameSort(a.name, b.name)),
      }))
      .sort(
        (a, b) =>
          tableNameSort(a.tables[0].name, b.tables[0].name) || a.tables.length - b.tables.length
      );
  }
);

export const activeCalendarFloorplanTablesByRoomSelector = createSelector(
  activeCalendarFloorplanTablesSelector,
  (tables): Dictionary<dashboard.Table[]> => {
    return _.groupBy(tables, (table) => table.room);
  }
);

export const calendarFloorplanInventoryScheduleSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.inventorySchedule,
  (inventorySchedule) => inventorySchedule?.[0] ?? undefined
);

export default createSelector(
  activeCalendarFloorplanTablesSelector,
  archivedCalendarFloorplanTablesSelector,
  allCalendarFloorplanTableCombinationsSelector,
  (activeTables, archivedTables, allTableCombinations): CalendarFloorplan => {
    return {
      activeTables,
      allTableCombinations,
      archivedTables,
    };
  }
);

export type TicketTypeIdsByTimeByWeekday = { [key: number]: TicketTypeIdsByLocalTimeString };

export type TicketTypeIdsByLocalTimeString = { [key: string]: number[] };

/**
 * Returns a map of ticket type ids excluded from pacing by start time, and by weekday values
 * (i.e., the Weekday.proto enum values from the server)
 */
export const ticketTypesExcludedFromPacingViaInventoryScheduleSelector: Selector<
  StoreType,
  TicketTypeIdsByTimeByWeekday
> = createSelector(
  (state: StoreType) => state.calendarFloorplan.inventorySchedule,
  (state: StoreType): Features => state.features.features,
  (inventorySchedule, features) => {
    if (!features.TACO_PACING_EXCLUDE_EXPERIENCE) {
      return {};
    }

    const excludedTicketTypeIdsByTimeByWeekday: TicketTypeIdsByTimeByWeekday = {};

    const schedule = inventorySchedule?.[0];

    schedule?.seatingTemplate.forEach((service) => {
      if (!service.weekday) {
        return;
      }

      const weekdaysForService = (service.weekday ?? []).map((weekday) => weekdayNumber(weekday));

      weekdaysForService.forEach((weekday) =>
        addExcludedExperiencesForServiceOnWeekday(
          weekday,
          service,
          excludedTicketTypeIdsByTimeByWeekday
        )
      );
    });

    return excludedTicketTypeIdsByTimeByWeekday;
  }
);

function addExcludedExperiencesForServiceOnWeekday(
  weekday: number,
  service: dashboard.ISeatingTemplate,
  excludedTicketTypeIdsByTimeByWeekday: TicketTypeIdsByTimeByWeekday
) {
  if (!service.startTime || !service.endTime) {
    return;
  }
  const serviceStartTime = fastParseTime(service.startTime);
  const serviceEndTime = fastParseTime(service.endTime);

  const ticketTypeIdsExcludedFromPacingForService =
    getPacingExcludedTicketTypeIdsForService(service);

  for (
    let time = businessTime(serviceStartTime);
    !time.isAfter(businessTime(serviceEndTime));
    time = time.plusMinutes(15)
  ) {
    const localTimeString = time.toLocalTime().toString();
    excludedTicketTypeIdsByTimeByWeekday[weekday] = {
      ...excludedTicketTypeIdsByTimeByWeekday[weekday],
      [localTimeString]: [
        ...(excludedTicketTypeIdsByTimeByWeekday[weekday]?.[localTimeString] || []),
        ...ticketTypeIdsExcludedFromPacingForService,
      ],
    };
  }
}

export type TicketTypesByService = Map<dashboard.ISeatingTemplate, core.TicketType[]>;

export const pacingExcludedTicketTypesByServiceSelector = createSelector(
  (state: StoreType) => state.calendarFloorplan.inventorySchedule,
  activeTicketsByIdSelector,
  (state: StoreType): Features => state.features.features,
  (state: StoreType, date: LocalDate) => date,
  (inventorySchedule, ticketsById, features, date) => {
    if (!features.TACO_PACING_EXCLUDE_EXPERIENCE) {
      return new Map();
    }

    const schedule = inventorySchedule?.[0];

    const excludedTicketTypeIdsByLocalTime: TicketTypesByService = new Map();

    const currentWeekdayValue = toProtoWeekdayNumber(date);

    schedule?.seatingTemplate
      .filter((service) =>
        (
          service.weekday?.filter(filterNilValues).map((weekday) => weekdayNumber(weekday)) ?? []
        ).includes(currentWeekdayValue)
      )
      .map((service) => {
        const ticketsExcludedFromPacingForService = getPacingExcludedTicketTypeIdsForService(
          service
        )
          .map((ticketTypeId) => ticketsById[ticketTypeId])
          .filter(filterNilValues);

        excludedTicketTypeIdsByLocalTime.set(service, ticketsExcludedFromPacingForService);
      });

    return excludedTicketTypeIdsByLocalTime;
  }
);

function getPacingExcludedTicketTypeIdsForService(service: dashboard.ISeatingTemplate) {
  return (
    service.experiencePacingBasic
      ?.filter((experiencePacingBasic) => experiencePacingBasic.excludeFromPacing)
      .map((experiencePacingBasic) => Number(experiencePacingBasic.ticketTypeId))
      .filter(filterNilValues) ?? []
  );
}
